import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { SmartGoalDoneStep } from '../../components/goals/wizard/SmartGoalDoneStep';
import { SmartGoalEssentialStep } from '../../components/goals/wizard/SmartGoalEssentialStep';
import { SmartGoalTypeStep } from '../../components/goals/wizard/SmartGoalTypeStep';
import { SmartGoalVisionStep } from '../../components/goals/wizard/SmartGoalVisionStep';
import { SmartGoalWhyStep } from '../../components/goals/wizard/SmartGoalWhyStep';
import { WizardSkeleton, WizardStepFooter } from '../../components/wizard';
import { useBudgetOverviewQuery } from '../../features/budgets/queries';
import {
  useCreateGoalMutation,
  useGoalQuery,
  useUpdateGoalMutation,
} from '../../features/goals/queries';
import { buildCycleOverviewQueryArgs } from '../../hooks/useCycleFinancialOverview';
import { useAuth } from '../../store/AuthContext';
import { useMobilePageHeaderTitle } from '../../store/MobilePageHeaderContext';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import {
  buildGoalMonthlyPreview,
  monthValueToTargetDate,
  targetDateToMonthValue,
} from '../../utils/goalWizardUtils';
import { parsePtNumber } from '../../utils/money';
import { toLocalISODate } from '../../utils/selfiePeriod';
import pt from '../../locales/pt';
import { paths } from '../../routes/paths';

const STEPS = ['type', 'essential', 'why', 'vision', 'done'];
const ALLOCATION_VALUES = ['essential', 'lifestyle', 'personal_investment'];

export function SmartGoalWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEdit = Boolean(editId);
  const fromGuided = searchParams.get('from') === 'guided';
  const prefill = location.state?.prefill;
  const guidedReturn = `${paths.dashboard}?openGuidedFlow=1`;

  const { data: editGoal, isLoading: editLoading } = useGoalQuery(editId, {
    enabled: isEdit,
  });

  const [stepIndex, setStepIndex] = useState(isEdit ? 1 : 0);
  const [direction, setDirection] = useState('forward');
  const [hydrated, setHydrated] = useState(!isEdit);

  const [measurementType, setMeasurementType] = useState('monetary');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [monthValue, setMonthValue] = useState('');
  const [budgetAllocation, setBudgetAllocation] = useState('personal_investment');
  const [why, setWhy] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [initialImageUrl, setInitialImageUrl] = useState(null);
  const [checklistTasks, setChecklistTasks] = useState([{ id: null, title: '' }]);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [donePreviewUrl, setDonePreviewUrl] = useState(null);

  const createMutation = useCreateGoalMutation();
  const updateMutation = useUpdateGoalMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const { profile } = useAuth();
  const { currentCycle: activeCycle } = useSelfieCycle();
  const overviewArgs = useMemo(
    () => buildCycleOverviewQueryArgs(activeCycle, { includeArchivedWithSpending: false }),
    [activeCycle],
  );
  const { data: budgetOverview } = useBudgetOverviewQuery(overviewArgs);

  const selfieDayOfMonth =
    profile?.selfie_day_of_month ??
    (activeCycle?.end ? Number.parseInt(activeCycle.end.split('-')[2], 10) : 27);

  useEffect(() => {
    if (isEdit || !prefill) return;
    if (prefill.name) setName(prefill.name);
    if (prefill.measurementType) setMeasurementType(prefill.measurementType);
    if (
      prefill.budgetAllocation &&
      ALLOCATION_VALUES.includes(prefill.budgetAllocation)
    ) {
      setBudgetAllocation(prefill.budgetAllocation);
    }
    if (prefill.name || prefill.measurementType) setStepIndex(1);
  }, [isEdit, prefill]);

  useEffect(() => {
    if (!isEdit || !editGoal || hydrated) return;
    setMeasurementType(editGoal.measurement_type || 'monetary');
    setName(editGoal.name || '');
    setAmount(
      editGoal.target_amount != null
        ? String(editGoal.target_amount).replace('.', ',')
        : '',
    );
    setMonthValue(targetDateToMonthValue(editGoal.target_date));
    if (ALLOCATION_VALUES.includes(editGoal.budget_allocation)) {
      setBudgetAllocation(editGoal.budget_allocation);
    }
    setWhy(editGoal.why || '');
    setInitialImageUrl(editGoal.image_url || null);
    const items = editGoal.checklist_items;
    setChecklistTasks(
      items?.length
        ? items.map((item) => ({ id: item.id, title: item.title }))
        : [{ id: null, title: '' }],
    );
    setStepIndex(1);
    setHydrated(true);
  }, [isEdit, editGoal, hydrated]);

  useEffect(() => {
    if (!imageFile) {
      setDonePreviewUrl(initialImageUrl);
      return undefined;
    }
    const url = URL.createObjectURL(imageFile);
    setDonePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile, initialImageUrl]);

  const isChecklist = measurementType === 'checklist';
  const stepKey = STEPS[stepIndex];
  const isDone = stepKey === 'done';
  const copy = pt.goals.wizard;
  useMobilePageHeaderTitle(isEdit ? copy.titleEdit : copy.title);

  const excludePotMonthly = useMemo(() => {
    if (!isEdit || !editGoal) return 0;
    const raw = editGoal.monthly_required ?? editGoal.monthly_amount ?? null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [isEdit, editGoal]);

  const monthlyPreview = useMemo(
    () =>
      buildGoalMonthlyPreview({
        amountInput: amount,
        monthValue,
        groups: budgetOverview?.groups,
        startDate: toLocalISODate(),
        selfieDayOfMonth,
        cycleStartISO: activeCycle?.start ?? toLocalISODate(),
        excludePotMonthly,
      }),
    [
      amount,
      monthValue,
      budgetOverview?.groups,
      selfieDayOfMonth,
      activeCycle?.start,
      excludePotMonthly,
    ],
  );

  const goTo = useCallback((nextIndex) => {
    setDirection(nextIndex < stepIndex ? 'back' : 'forward');
    setStepIndex(nextIndex);
  }, [stepIndex]);

  const onBack = useCallback(() => {
    if (stepIndex === 0 || (isEdit && stepIndex <= 1)) {
      if (fromGuided) {
        navigate(guidedReturn);
        return;
      }
      navigate(isEdit && editId ? paths.goal(editId) : paths.goals);
      return;
    }
    goTo(stepIndex - 1);
  }, [editId, fromGuided, goTo, guidedReturn, isEdit, navigate, stepIndex]);

  const buildPayload = useCallback(
    ({ includeImage } = { includeImage: true }) => {
      const targetDate = monthValueToTargetDate(monthValue);
      let payload;
      if (isChecklist) {
        payload = {
          name: name.trim(),
          measurement_type: 'checklist',
          target_date: targetDate,
          why: why || '',
          description: '',
          checklist_items: checklistTasks
            .map((t) => ({ id: t.id || undefined, title: t.title.trim() }))
            .filter((t) => t.title),
        };
      } else {
        const target = parsePtNumber(amount);
        payload = {
          name: name.trim(),
          measurement_type: 'monetary',
          target_amount: target.toFixed(2),
          target_date: targetDate,
          budget_allocation: budgetAllocation,
          why: why || '',
          description: '',
        };
      }
      if (includeImage && imageFile) payload.image = imageFile;
      return payload;
    },
    [
      amount,
      budgetAllocation,
      checklistTasks,
      imageFile,
      isChecklist,
      monthValue,
      name,
      why,
    ],
  );

  const persistGoal = useCallback(
    async ({ includeImage } = { includeImage: true }) => {
      const payload = buildPayload({ includeImage });
      if (isEdit) {
        return updateMutation.mutateAsync({ id: editId, ...payload });
      }
      return createMutation.mutateAsync(payload);
    },
    [buildPayload, createMutation, editId, isEdit, updateMutation],
  );

  const validateEssential = useCallback(() => {
    if (!name.trim()) {
      toast.error(copy.essential.nameRequired);
      return false;
    }
    if (!monthValue) {
      toast.error(copy.essential.dateRequired);
      return false;
    }
    if (isChecklist) {
      const items = checklistTasks.map((t) => t.title.trim()).filter(Boolean);
      if (items.length === 0) {
        toast.error(pt.goals.checklist.emptyTask);
        return false;
      }
    } else {
      const target = parsePtNumber(amount);
      if (target == null || target <= 0) {
        toast.error(copy.essential.amountRequired);
        return false;
      }
    }
    return true;
  }, [amount, checklistTasks, copy.essential, isChecklist, monthValue, name]);

  const onContinue = useCallback(async () => {
    try {
      if (stepKey === 'type') {
        goTo(1);
        return;
      }
      if (stepKey === 'essential') {
        if (!validateEssential()) return;
        goTo(2);
        return;
      }
      if (stepKey === 'why') {
        goTo(3);
        return;
      }
      if (stepKey === 'vision') {
        await persistGoal({ includeImage: true });
        setSavedSnapshot({
          name: name.trim(),
          why,
          isChecklist,
          amount: isChecklist ? null : parsePtNumber(amount),
          monthValue,
          monthly: isChecklist ? null : monthlyPreview.monthly,
        });
        goTo(4);
        return;
      }
      if (stepKey === 'done') {
        if (fromGuided) {
          navigate(guidedReturn, { replace: true });
        } else if (isEdit && editId) {
          navigate(paths.goal(editId), { replace: true });
        } else {
          navigate(paths.goals, { replace: true });
        }
      }
    } catch (err) {
      const message =
        (err && (err.detail || err.target_date || err.target_amount || err.checklist_items)) ||
        'Não foi possível guardar o objectivo.';
      toast.error(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, [
    amount,
    editId,
    fromGuided,
    goTo,
    guidedReturn,
    isChecklist,
    isEdit,
    monthlyPreview.monthly,
    monthValue,
    name,
    navigate,
    persistGoal,
    stepKey,
    validateEssential,
    why,
  ]);

  const onSkipImage = useCallback(async () => {
    try {
      await persistGoal({ includeImage: false });
      setSavedSnapshot({
        name: name.trim(),
        why,
        isChecklist,
        amount: isChecklist ? null : parsePtNumber(amount),
        monthValue,
        monthly: isChecklist ? null : monthlyPreview.monthly,
      });
      goTo(4);
    } catch (err) {
      const message =
        (err && (err.detail || err.target_date || err.target_amount)) ||
        'Não foi possível guardar o objectivo.';
      toast.error(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, [
    amount,
    goTo,
    isChecklist,
    monthlyPreview.monthly,
    monthValue,
    name,
    persistGoal,
    why,
  ]);

  const footerLabel = useMemo(() => {
    if (stepKey === 'vision') return isEdit ? copy.saveCta : copy.createCta;
    if (stepKey === 'done') return isEdit ? copy.doneCtaDetail : copy.doneCta;
    return copy.continueCta;
  }, [stepKey, isEdit, copy]);

  if (isEdit && (editLoading || !hydrated)) {
    return (
      <WizardSkeleton
        appChrome
        wizardTitle={copy.titleEdit}
        step={2}
        total={STEPS.length}
        showProgress
        showPercent={false}
        loading
      >
        <p className="text-sm text-muted-foreground">{pt.common.loading}</p>
      </WizardSkeleton>
    );
  }

  const snap = savedSnapshot;

  return (
    <WizardSkeleton
      appChrome
      wizardTitle={isEdit ? copy.titleEdit : copy.title}
      step={stepIndex + 1}
      total={STEPS.length}
      onBack={onBack}
      showBack={!isDone}
      direction={direction}
      loading={isPending}
      showProgress={!isDone}
      showPercent={false}
      footer={
        <WizardStepFooter
          primaryLabel={footerLabel}
          onPrimary={onContinue}
          onSkip={stepKey === 'vision' ? onSkipImage : undefined}
          skipLabel={copy.skipImage}
          loading={isPending}
        />
      }
    >
      {stepKey === 'type' ? (
        <SmartGoalTypeStep
          measurementType={measurementType}
          onSelect={setMeasurementType}
        />
      ) : null}
      {stepKey === 'essential' ? (
        <SmartGoalEssentialStep
          isChecklist={isChecklist}
          name={name}
          onNameChange={setName}
          amount={amount}
          onAmountChange={setAmount}
          monthValue={monthValue}
          onMonthChange={setMonthValue}
          monthlyPreview={monthlyPreview}
          budgetAllocation={budgetAllocation}
          onBudgetAllocationChange={setBudgetAllocation}
          checklistTasks={checklistTasks}
          onUpdateTask={(index, value) => {
            setChecklistTasks((tasks) =>
              tasks.map((t, i) => (i === index ? { ...t, title: value } : t)),
            );
          }}
          onAddTask={() =>
            setChecklistTasks((tasks) => [...tasks, { id: null, title: '' }])
          }
          onRemoveTask={(index) => {
            setChecklistTasks((tasks) =>
              tasks.length <= 1
                ? [{ id: null, title: '' }]
                : tasks.filter((_, i) => i !== index),
            );
          }}
        />
      ) : null}
      {stepKey === 'why' ? <SmartGoalWhyStep why={why} onChange={setWhy} /> : null}
      {stepKey === 'vision' ? (
        <SmartGoalVisionStep
          imageFile={imageFile}
          initialUrl={initialImageUrl}
          onChange={setImageFile}
          goalName={name}
          isChecklist={isChecklist}
          amountValue={parsePtNumber(amount) || 0}
          monthValue={monthValue}
        />
      ) : null}
      {stepKey === 'done' && snap ? (
        <SmartGoalDoneStep
          isEdit={isEdit}
          isChecklist={snap.isChecklist}
          name={snap.name}
          why={snap.why}
          imageUrl={donePreviewUrl}
          amount={snap.amount}
          monthValue={snap.monthValue}
          monthly={snap.monthly}
        />
      ) : null}
    </WizardSkeleton>
  );
}

export default SmartGoalWizard;
